import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, Globe, Upload, X, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useData } from "@/context/DataContext";
import { api, formatApiError } from "@/lib/api";
import { track } from "@/lib/track";
import Seo from "@/components/site/Seo";
import { PageHero } from "@/components/site/Layout";
import { Reveal } from "@/components/site/Reveal";
import { WhatsAppButton } from "@/components/site/WhatsButton";

const TIPOLOGIE = ["Manutenzione giardino", "Trattamento fitosanitario", "Potatura", "Abbattimento", "Costruzione nuovo giardino", "Rifacimento giardino", "Altro"];
const MAX = 8 * 1024 * 1024;

export default function Contact() {
  const { settings } = useData();
  const [form, setForm] = useState({ nome: "", telefono: "", email: "", comune: "", tipologia: "", descrizione: "", preferenza_contatto: "", privacy: false });
  const [hp, setHp] = useState("");
  const [errors, setErrors] = useState({});
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const validate = () => {
    const e = {};
    if (!form.nome.trim()) e.nome = "Inserisci nome e cognome";
    if (!form.telefono.trim()) e.telefono = "Inserisci un numero di telefono";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Inserisci un'email valida";
    if (!form.tipologia) e.tipologia = "Seleziona una tipologia";
    if (!form.descrizione.trim()) e.descrizione = "Descrivi la tua richiesta";
    if (!form.privacy) e.privacy = "È necessario accettare l'informativa sulla privacy";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onFiles = async (list) => {
    const arr = Array.from(list);
    setUploading(true);
    try {
      for (const file of arr) {
        if (!file.type.startsWith("image/")) { toast.error("Sono ammesse solo immagini"); continue; }
        if (file.size > MAX) { toast.error(`"${file.name}" supera 8MB`); continue; }
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((prev) => [...prev, { name: file.name, url: data.url }]);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore durante il caricamento");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) { toast.error("Controlla i campi evidenziati"); return; }
    setSubmitting(true);
    try {
      await api.post("/contact", {
        ...form,
        allegati: files.map((f) => f.url),
        website: hp,
        pagina_provenienza: typeof window !== "undefined" ? window.location.href : "",
      });
      setDone(true);
      track("form_submit", form.tipologia);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Invio non riuscito");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo title="Contatti" description="Contatta Animaverde per un sopralluogo gratuito. Telefono e WhatsApp 348 9904574, email animaverdensc@gmail.com." path="/contatti" />
      <PageHero
        overline="Parla con Animaverde"
        title="Richiedi un sopralluogo gratuito"
        subtitle="Raccontaci di cosa hai bisogno. Ti ricontatteremo per organizzare una valutazione senza impegno."
        breadcrumb={<><Link to="/" className="hover:text-av-lime">Home</Link> / <span className="text-av-white">Contatti</span></>}
      />

      <section className="bg-av-black py-16 sm:py-24">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid lg:grid-cols-[0.8fr_1.2fr] gap-14">
          {/* contacts */}
          <Reveal>
            <div className="space-y-4">
              {settings && (
                <>
                  <a href={`tel:${settings.telefono_link}`} data-testid="contact-call" className="flex items-center gap-4 rounded-2xl border border-white/10 p-5 hover:border-av-lime/50 transition-colors">
                    <span className="w-11 h-11 rounded-full bg-av-bright/15 text-av-lime flex items-center justify-center"><Phone size={18} /></span>
                    <span><span className="block text-xs text-av-gray">Chiama ora</span><span className="text-av-white font-semibold">{settings.telefono}</span></span>
                  </a>
                  <a href={`mailto:${settings.email}`} data-testid="contact-email" className="flex items-center gap-4 rounded-2xl border border-white/10 p-5 hover:border-av-lime/50 transition-colors">
                    <span className="w-11 h-11 rounded-full bg-av-bright/15 text-av-lime flex items-center justify-center"><Mail size={18} /></span>
                    <span><span className="block text-xs text-av-gray">Invia email</span><span className="text-av-white font-semibold break-all">{settings.email}</span></span>
                  </a>
                  <div className="flex items-center gap-4 rounded-2xl border border-white/10 p-5">
                    <span className="w-11 h-11 rounded-full bg-av-bright/15 text-av-lime flex items-center justify-center"><Globe size={18} /></span>
                    <span><span className="block text-xs text-av-gray">Sito web</span><span className="text-av-white font-semibold">{settings.sito}</span></span>
                  </div>
                  <div className="pt-2"><WhatsAppButton className="w-full justify-center" /></div>
                </>
              )}
            </div>
          </Reveal>

          {/* form */}
          <Reveal delay={0.1}>
            {done ? (
              <div data-testid="contact-success" className="rounded-3xl border border-av-lime/40 bg-av-green-dark/40 p-10 text-center" style={{ backgroundColor: "rgba(22,56,29,0.4)" }}>
                <span className="mx-auto w-14 h-14 rounded-full bg-av-bright text-av-black flex items-center justify-center mb-5"><Check size={26} strokeWidth={3} /></span>
                <h2 className="font-display text-3xl text-av-white">Grazie!</h2>
                <p className="mt-3 text-av-gray">La richiesta è stata inviata correttamente. Ti ricontatteremo appena possibile.</p>
              </div>
            ) : (
              <form onSubmit={submit} noValidate data-testid="contact-form" className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label="Nome e cognome *" error={errors.nome}>
                    <input data-testid="field-nome" value={form.nome} onChange={(e) => set("nome", e.target.value)} className={inputCls(errors.nome)} />
                  </Field>
                  <Field label="Numero di telefono *" error={errors.telefono}>
                    <input data-testid="field-telefono" value={form.telefono} onChange={(e) => set("telefono", e.target.value)} className={inputCls(errors.telefono)} inputMode="tel" />
                  </Field>
                  <Field label="Email *" error={errors.email}>
                    <input data-testid="field-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls(errors.email)} />
                  </Field>
                  <Field label="Comune" error={errors.comune}>
                    <input data-testid="field-comune" value={form.comune} onChange={(e) => set("comune", e.target.value)} className={inputCls()} />
                  </Field>
                </div>

                <Field label="Tipologia di intervento *" error={errors.tipologia}>
                  <select data-testid="field-tipologia" value={form.tipologia} onChange={(e) => set("tipologia", e.target.value)} className={`${inputCls(errors.tipologia)} appearance-none`}>
                    <option value="" className="bg-av-black">Seleziona…</option>
                    {TIPOLOGIE.map((t) => <option key={t} value={t} className="bg-av-black">{t}</option>)}
                  </select>
                </Field>

                <Field label="Descrizione della richiesta *" error={errors.descrizione}>
                  <textarea data-testid="field-descrizione" rows={4} value={form.descrizione} onChange={(e) => set("descrizione", e.target.value)} className={`${inputCls(errors.descrizione)} resize-none`} />
                </Field>

                <Field label="Come preferisci essere ricontattato?">
                  <select data-testid="field-preferenza" value={form.preferenza_contatto} onChange={(e) => set("preferenza_contatto", e.target.value)} className={`${inputCls()} appearance-none`}>
                    <option value="" className="bg-av-black">Indifferente</option>
                    <option value="Telefono" className="bg-av-black">Telefono</option>
                    <option value="WhatsApp" className="bg-av-black">WhatsApp</option>
                    <option value="Email" className="bg-av-black">Email</option>
                  </select>
                </Field>

                {/* honeypot antispam (nascosto) */}
                <div data-testid="honeypot-wrap" aria-hidden="true" style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, overflow: "hidden" }}>
                  <label>Non compilare questo campo
                    <input data-testid="field-website" type="text" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} />
                  </label>
                </div>

                {/* attachments */}
                <div>
                  <label className="block text-sm text-av-white/80 mb-3">Allega fotografie del giardino</label>
                  <label data-testid="field-upload" className="flex items-center justify-center gap-3 rounded-xl border border-dashed border-white/25 p-6 cursor-pointer hover:border-av-lime/60 transition-colors text-av-gray">
                    {uploading ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
                    <span className="text-sm">{uploading ? "Caricamento…" : "Trascina o seleziona immagini (max 8MB)"}</span>
                    <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} />
                  </label>
                  {files.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {files.map((f, i) => (
                        <span key={i} className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-xs text-av-white">
                          {f.name}
                          <button type="button" onClick={() => setFiles((p) => p.filter((_, idx) => idx !== i))} className="text-av-gray hover:text-red-400"><X size={13} /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* privacy */}
                <div>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input data-testid="field-privacy" type="checkbox" checked={form.privacy} onChange={(e) => set("privacy", e.target.checked)} className="mt-1 w-4 h-4 accent-[#2ea33f]" />
                    <span className="text-sm text-av-gray">Ho letto e accetto l'<Link to="/privacy-policy" className="text-av-lime underline">informativa sulla privacy</Link> e acconsento al trattamento dei dati per essere ricontattato. *</span>
                  </label>
                  {errors.privacy && <p className="mt-1.5 text-xs text-red-400" data-testid="error-privacy">{errors.privacy}</p>}
                </div>

                <button type="submit" data-testid="contact-submit" disabled={submitting}
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-av-bright text-av-black px-8 py-4 text-sm font-semibold hover:bg-av-lime transition-colors disabled:opacity-60 w-full sm:w-auto">
                  {submitting ? <><Loader2 size={16} className="animate-spin" /> Invio in corso…</> : "Invia la richiesta"}
                </button>
                <p className="text-xs text-av-gray/70">Il modulo invia una richiesta di contatto. Non fornisce preventivi automatici.</p>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </>
  );
}

const inputCls = (err) =>
  `w-full bg-transparent border-0 border-b ${err ? "border-red-400" : "border-white/25"} focus:border-av-lime outline-none py-2.5 text-av-white placeholder:text-av-gray/50 transition-colors`;

const Field = ({ label, error, children }) => (
  <div>
    <label className="block text-sm text-av-white/80 mb-1.5">{label}</label>
    {children}
    {error && <p className="mt-1.5 text-xs text-red-400">{error}</p>}
  </div>
);
